import { z } from 'zod';
import { getNrpOption } from '@features/components/CaseFormBlocks';
import { caseCommonFields, reallocationFields } from '@features/forms/CaseForms/schema';
import {
  validateDirectAcquisitionRequiredFields,
  validateDonorId,
  validateOpoContact,
  validateReallocation,
  validateTxCenterId,
} from '@features/forms/CaseForms/validators';
import { Case, ClinicalItemType, Laterality, NRPType } from '@/api/apis/case';
import { getClinicalRecordByType } from '@/utils/helpers';
import { NA_REGION_ID } from '@/constants/serviceRegion.constants';

const baseSchema = z.object({
  ...caseCommonFields,
  ...reallocationFields,
  opoId: z.string(),
  opoContactId: z.string().nullish(),
  donorHospitalId: z.string(),
  txCenterId: z.string(),
  txCenterTrialPrograms: z.array(z.any()).optional(),
  opoRequest: z.boolean(),
  originalOpoRequest: z.boolean().optional(),
  additionalServices: z.boolean(),
});

export const caseGeneralSchema = baseSchema.superRefine((formData, context) => {
  validateDonorId(formData, context);
  validateReallocation(formData, context);
  validateDirectAcquisitionRequiredFields(formData, context);

  validateOpoContact(formData, context, 'opoRequest', 'Opo request should be checked if OPO contact is not specified');
  validateTxCenterId(formData, context, 'txCenterId', 'Opo request should be checked if TX center is not specified');
});

export type CaseGeneralFormType = z.infer<typeof caseGeneralSchema>;

export const caseGeneralInitialValues = (caseData: Case): CaseGeneralFormType => {
  const {
    donorId,
    donorType,
    serviceType,
    serviceDetails,
    organType,
    laterality,
    opoRequest,
    reAllocationCaseId,
    txCenterDetails,
    clinicalData,
    assignedOpoContacts,
    additionalServices,
    isExpeditedCase,
    isQuestionaryEditAllowed,
    serviceRegionId,
    opoId,
    donorHospitalId,
  } = caseData;

  const nrpData = getClinicalRecordByType(clinicalData ?? [], ClinicalItemType.NRPApplicabilityAndType);
  const nrp = nrpData?.item?.yesNo;
  const nrpType = nrpData?.item?.nrpType;
  const opoContactId = assignedOpoContacts.find((c) => c.isPrimary);

  return {
    isExpeditedCase: isExpeditedCase ?? false,
    txCenterId: txCenterDetails?.id ?? '',
    txCenterTrialPrograms: txCenterDetails?.trialPrograms ?? [],
    donorId,
    donorType,
    serviceType,
    serviceDetails: serviceDetails ?? '',
    organType,
    laterality: laterality ?? Laterality.NotDefined,
    opoRequest,
    originalOpoRequest: opoRequest,
    reAllocationCase: !!reAllocationCaseId,
    reAllocationCaseId,
    originalReAllocationCaseId: reAllocationCaseId,
    nrp: getNrpOption(nrp),
    nrpType: !nrpType || nrpType === NRPType.none ? NRPType.aNrp : nrpType,
    opoContactId: opoContactId?.id,
    additionalServices,
    serviceRegionId: serviceRegionId ?? NA_REGION_ID,
    isQuestionaryEditAllowed: isQuestionaryEditAllowed ?? false,
    opoId: opoId || '',
    donorHospitalId: donorHospitalId || '',
  };
};
